import React, { useState, useContext, useEffect } from 'react';
import { Formik, Form, useField } from 'formik';
import { keyframes } from '@emotion/react';
import * as Yup from 'yup';
import { Box, Button, Checkbox, FormControlLabel, Typography, IconButton, CircularProgress, useTheme } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import skillManagementImage from '../../Assets/skill_managementImage.jpg';
import logo from '../../Assets/vantris-energy-logo-1x.png';
import FormInput from '../../SystemDesign/FormInput';
import { SnackBarContext } from '../../Context/SnackBarContextProvider';
import { useAuth } from '../../Context/AuthContext';
import DialogResetPassword from './DialogResetPassword';
import { tokens } from '../../theme';

const Login = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const leftPanelBackground = `radial-gradient(circle at 30% 30%, ${colors.primary[850]}, ${colors.primary[850]})`;
  
  // State for password visibility
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  // State for password reset dialog
  const [openResetDialog, setOpenResetDialog] = useState(false);
  // Initialize navigation and login mutation
  const navigate = useNavigate();
    const location = useLocation();

    const { login, isLoading, user, isAuth } = useAuth();

  // Prefill email (username) when coming from switchAccount
  const [prefilledEmail, setPrefilledEmail] = useState('');
  useEffect(() => {
        const key = 'prefillEmail';
        try {
            const stored = localStorage.getItem(key);
            if (stored) setPrefilledEmail(stored);
        } catch (_) { }
        try { localStorage.removeItem(key); } catch (_) { }
  }, []);

  useEffect(() => {
        // Only navigate if authentication is successful AND user data is available
        if (isAuth && user) {
            let redirectTo = location.state?.from?.pathname;

            if (typeof location.state?.from === 'string') {
                redirectTo = location.state.from;
            } else if (location.state?.from && typeof location.state.from === 'object' && location.state.from.pathname) {
                redirectTo = location.state.from.pathname;
            } else if (!redirectTo) { // If no specific 'from' state
                redirectTo = '/dashboard';
            }

            if (redirectTo) {
                navigate(redirectTo, { replace: true });
            }
        }
    }, [isAuth, user, navigate, location.state]);

  // Access contexts
  const { setInfoAlert, setOpenSnackBar } = useContext(SnackBarContext);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  
  const handleRememberMe = (event) => {
    setRememberMe(event.target.checked);
  };
  
  // Handlers for password reset dialog
  const handleOpenResetDialog = () => {
    setOpenResetDialog(true);
  };
  
  const handleCloseResetDialog = () => {
    setOpenResetDialog(false);
  };

  // Define circle animations
  const renderCircles = () => {
    return [...Array(8)].map((_, index) => {
      const floatAnimation = keyframes`
        0% { transform: translate(0, 0) rotate(0deg); }
        25% { transform: translate(${10 + index * 5}px, -${15 + index * 3}px) rotate(${index * 15}deg); }
        50% { transform: translate(-${5 + index * 4}px, ${10 + index * 5}px) rotate(${index * 30}deg); }
        75% { transform: translate(-${15 + index * 3}px, -${5 + index * 4}px) rotate(${index * 45}deg); }
        100% { transform: translate(0, 0) rotate(0deg); }
      `;

      const pulseAnimation = keyframes`
        0%, 100% { transform: scale(1); opacity: ${0.2 + index * 0.05}; }
        50% { transform: scale(1.1); opacity: ${0.3 + index * 0.05}; }
      `;

      const size = 80 + index * 15;
      const positions = [
        { top: '15%', left: '15%' },
        { top: '25%', left: '85%' },
        { top: '75%', left: '20%' },
        { top: '65%', left: '80%' },
        { top: '45%', left: '10%' },
        { top: '35%', left: '90%' },
        { top: '85%', left: '45%' },
        { top: '10%', left: '50%' }
      ];

      return (
        <Box
          key={index}
          sx={{
            position: 'absolute',
            width: `${size}px`,
            height: `${size}px`,
            borderRadius: '50%',
            background: `radial-gradient(circle at 30% 30%,${colors.primary[850]}, ${colors.primary[800]})`,
            boxShadow: '0 0 15px 5px rgba(0, 188, 212, 0.2)',
            opacity: 0.7,
            top: positions[index].top,
            left: positions[index].left,
            animation: `${floatAnimation} ${4 + index}s infinite ease-in-out, ${pulseAnimation} ${3 + index * 0.5}s infinite ease-in-out alternate`,
            zIndex: 0,
            pointerEvents: 'none'
          }}
        />
      );
    });
  };
  const validationSchema = Yup.object().shape({
    username: Yup.string().required('Username is required'),
    password: Yup.string().required('Password is required'),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      await login(values.username, values.password);
    } catch (error) {
      setInfoAlert({
        severity: 'error',
        message: error.data?.message || 'Login failed. Please check your credentials.'
      });
      setOpenSnackBar(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          height: '100vh',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          filter: openResetDialog ? 'blur(4px)' : 'none',
          opacity: openResetDialog ? 0.6 : 1,
          transition: 'filter 0.3s ease, opacity 0.3s ease',
          pointerEvents: openResetDialog ? 'none' : 'auto',
          // Make sure the login content stays below the dialog
          zIndex: 1
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            width: { xs: '95%', sm: '80%' },
            maxWidth: '900px',
            height: { xs: 'auto', md: '75vh' },
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {/* Left Section - With Animated Circles Background and Centered Image */}
          <Box
            sx={{
              width: { xs: '100%', md: '60%' },
              height: { xs: '30vh', md: '100%' },
              position: 'relative',
              overflow: 'hidden',
              background: leftPanelBackground,
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
            }}
          >
            {/* Animated Circles Overlay for entire left panel */}
            {renderCircles()}
            
            {/* Top Section with Text */}
            <Box
              sx={{
                height: '50%',
                width: '100%',
                padding: '40px 30px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                zIndex: 1,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: colors.primary[90],
                  textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  fontFamily:"Ancizar Sans"
                }}
              >
                Welcome To
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  color: '#ffffff',
                  fontWeight: 'bold',
                  mb: 1,
                  fontFamily:"Josefin Sans",
                  textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                }}
              >
                VANTRIS ENERGY
              </Typography>
              <Typography
                sx={{
                  fontFamily:"Montserrat",
                  color: '#ffffff',
                  fontSize: '1rem',
                  lineHeight: 1.5,
                  textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                }}
              >
                Competency management platform to optimize performance and develop your team's expertise.
              </Typography>
            </Box>
            
            {/* Bottom Section with Image */}
            <Box
              sx={{
                height: '100%',
                width: '100%',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                objectFit: 'cover',
                opacity: 0.9,
                justifyContent: 'center',
              }}
            >
              <Box
                component="img"
                src={skillManagementImage}
                alt="Skill Management"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  //borderRadius: '8px',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                }}
              />
            </Box>
          </Box>

          {/* Right: Login Form */}
          <Box
            sx={{
              width: { xs: '100%', md: '60%' },
              height: { xs: 'auto', md: '100%' },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: theme.palette.background.secondCard,
              padding: '20px',
              boxSizing: 'border-box',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Bottom Right Corner Circles */}
            {[...Array(3)].map((_, index) => {
              const size = 70 + index * 20;
              const positions = [
                { bottom: '-20px', right: '-20px' },
                { bottom: '10px', right: '-40px' },
                { bottom: '-30px', right: '20px' },
              ];
              
              const floatAnimation = keyframes`
                0% { transform: translate(0, 0) rotate(0deg); }
                50% { transform: translate(${3 + index * 2}px, -${5 + index * 2}px) rotate(${index * 5}deg); }
                100% { transform: translate(0, 0) rotate(0deg); }
              `;
              
              return (
                <Box
                  key={`right-corner-circle-${index}`}
                  sx={{
                    position: 'absolute',
                    width: `${size}px`,
                    height: `${size}px`,
                    borderRadius: '50%',
                    background: `radial-gradient(circle at 30% 30%, ${colors.primary[20]}, ${colors.primary[300]})`,
                    boxShadow: '0 0 15px 5px rgba(0, 188, 212, 0.1)',
                    opacity: 0.2,
                    bottom: positions[index].bottom,
                    right: positions[index].right,
                    animation: `${floatAnimation} ${4 + index}s infinite ease-in-out`,
                    zIndex: 0,
                    pointerEvents: 'none'
                  }}
                />
              );
            })}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box component="img" src={logo} alt="WWS Energies Logo" sx={{ height: 60 }} />
            </Box>
            <Typography variant="h4" sx={{ mb: 1, color: colors.primary[700], fontFamily:"Ancizar Sans"
,fontWeight:"bold"}}>
              Sign in
            </Typography>
            <Typography sx={{ mb: 3, color: colors.grey[600], fontSize: '1.1rem' }}>
            Enter your credentials to continue
            </Typography>
            <Formik
              initialValues={{ username: prefilledEmail || '', password: '' }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ errors, touched }) => (
                <Form style={{ width: '100%', maxWidth: '300px' }}>
                  <Box sx={{ mb: 2 }}>
                    <FormInput
                      fullWidth
                      id="username"
                      name="username"
                      label="Username"
                      type="text"
                      placeholder="Username"
                      // sx={{ background: '#f5f5f5', borderRadius: '8px' }}
                    />
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <FormInput
                      fullWidth
                      id="password"
                      name="password"
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Password"
                      // sx={{ background: '#f5f5f5', borderRadius: '8px' }}
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            onClick={togglePasswordVisibility}
                            edge="end"
                            size="small"
                            sx={{ color:colors.primary[600] }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        ),
                      }}
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Checkbox 
                        name="remember" 
                        checked={rememberMe}
                        onChange={handleRememberMe}
                        size="small"
                        sx={{ 
                          color: colors.primary[600], 
                          p: 0, 
                          mr: 1,
                          '&.Mui-checked': {
                            color: colors.primary[600]
                          } 
                        }} 
                      />
                      <Typography variant="body2" sx={{ color: colors.grey[600], fontSize: '0.9rem' }}>
                        Remember me
                      </Typography>
                    </Box>
                    <Button 
                      variant="text" 
                      sx={{ color: colors.primary[600], fontSize: '0.85rem' }}
                      onClick={handleOpenResetDialog}
                    >
                      Forgot password?
                    </Button>
                  </Box>
                  
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={isLoading}
                    sx={{
                    mt: 1.5,
                    py: 1,
                    borderRadius: '8px',
                    background: `linear-gradient(80deg, ${colors.secondary[600]} 0%, ${colors.secondary[750]} 100%)`,
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '1.15rem',
                    textTransform: 'none',
                    boxShadow: '0 4px 16px #b7eaff33',
                    transition: 'box-shadow 0.3s',
                    '&:hover': { background: `linear-gradient(90deg, ${colors.secondary[700]} 0%, ${colors.secondary[750]} 100%)`, boxShadow: '0 6px 24px #4db6d644' },
                  }}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Login'
                    )}
                  </Button>
                </Form>
              )}
            </Formik>
          </Box>
        </Box>
      </Box>
      
      {/* Place the dialog without a wrapper to ensure it works properly */}
      <DialogResetPassword 
        open={openResetDialog} 
        onClose={handleCloseResetDialog} 
      />
    </>
  );
};

export default Login;